import { z } from 'zod'
import { calendarDateSchema } from '../validators/calendar-date.validator.js'
import { uuidSchema } from '../validators/uuid.validator.js'

/**
 * One visit to a site: one assessment of its trees, its irrigation, or both,
 * and what it found. See docs/site-visits.md.
 */
export const siteVisitDtoSchema = z.object({
  id: uuidSchema,
  siteId: uuidSchema,
  /** The day it started, `YYYY-MM-DD`, as the phone that started it saw it. */
  startedOn: calendarDateSchema,
  coversTrees: z.boolean(),
  coversIrrigation: z.boolean(),
  startedByName: z.string().nullable(),
  /** When somebody said it was done. A marker, not a lock. */
  finishedAt: z.string().datetime().nullable(),
  notes: z.string().nullable(),
  /**
   * Ids other phones made for this visit: started on the same site the same
   * day, offline, and joined into it. A phone holding one of these uses this
   * visit's id from then on.
   */
  joinedIds: z.array(uuidSchema),
  /** What it found, for the site's list of visits. */
  treesAssessed: z.number().int().nonnegative(),
  treeProblems: z.number().int().nonnegative(),
  irrigationProblems: z.number().int().nonnegative(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
})

export type SiteVisitDto = z.infer<typeof siteVisitDtoSchema>

/** A site's visits, newest first. */
export const siteVisitListDtoSchema = z.object({
  siteId: uuidSchema,
  visits: z.array(siteVisitDtoSchema),
})

export type SiteVisitListDto = z.infer<typeof siteVisitListDtoSchema>

/** A visit, by the two facts a sentence about it needs. */
export const siteVisitRefDtoSchema = z.object({
  id: uuidSchema,
  startedOn: calendarDateSchema,
})

export type SiteVisitRefDto = z.infer<typeof siteVisitRefDtoSchema>
